// Die eigenen Arme in der Ego-Sicht: zwei Quaderpaare, die unten im Bild an der Kamera hängen und
// beim Gehen pendeln (tasks/task-011, docs/architektur-paket-2.md, Abschnitt „Avatar“).
//
// Sie hängen wirklich an der Kamera, sind also deren Kinder: So bleiben sie beim Umsehen genau da,
// wo sie sind, ohne dass jedes Bild eine Position ausgerechnet werden müsste. Damit Three.js
// Kinder einer Kamera überhaupt zeichnet, muss die Kamera selbst im Szenenbaum hängen – das
// erledigt `createArme` gleich mit.
//
// Die Maße und Farbbereiche kommen aus demselben Bauplan wie die ganze Figur
// (`avatarBauplan()` in shared): Was man an sich herunterschaut, ist genau das, was andere von
// vorn sehen. Nur zwei Bereiche kommen vor, Ärmel (Oberteil) und Unterarm (Haut) – ein Kopf im
// Bild wäre nur im Weg. In den Three.js-Objekten liegt kein Spielzustand (Architektur-Regel 1);
// Farben, Pendelwinkel und Sichtbarkeit setzt der Aufrufer.

import { BoxGeometry, Color, Group, Mesh, MeshLambertMaterial, type PerspectiveCamera, type Scene } from 'three';
import { AVATAR_PENDEL, avatarBauplan, type AvatarColors, type AvatarTeil } from '@bauernhof/shared';

/** Lage der Arme im Kamerabild. Die Kamera blickt nach −z; y wächst nach oben, x nach rechts. */
export const ARME = {
  /** Seitlicher Abstand der Schultern von der Blickachse in Blöcken. */
  seite: 0.28,
  /** Höhe der Schultern unter der Blickachse; negativ heißt „unten im Bild“. */
  hoehe: -0.2,
  /** Abstand der Schultern vor der Kamera; muss vor der Nahebene (0.1) liegen. */
  tiefe: -0.3,
  /** Ruhelage: So weit sind die Arme nach vorn gekippt, damit sie ins Bild ragen (Bogenmaß). */
  neigung: 1.15,
  /**
   * Wie stark der Gang der Figur auf die eigenen Arme durchschlägt. Die Arme sind hier nur eine
   * Handbreit vor dem Auge; der volle Ausschlag der Figur wäre ein Wischen quer durchs Bild.
   */
  pendel: 0.45,
} as const;

/** Nur diese beiden Glieder gehören ins eigene Bild. */
const ARM_TEILE: readonly AvatarTeil[] = ['arm links', 'arm rechts'];

export interface Arme {
  /** Färbt Haut und Oberteil um; sofort sichtbar, auch während der Spiegel offen ist. */
  setFarben(farben: AvatarColors): void;
  /** Pendelwinkel aus `createGang()` im Bogenmaß; 0 heißt stehen. */
  setSwing(swing: number): void;
  /** In der Schulterkamera sieht man die ganze Figur – dann verschwinden die eigenen Arme. */
  setSichtbar(sichtbar: boolean): void;
  /** Nimmt die Arme wieder aus der Kamera und gibt Geometrien und Materialien frei. */
  dispose(): void;
}

export function createArme(scene: Scene, camera: PerspectiveCamera, farben: AvatarColors): Arme {
  // Kinder einer Kamera werden nur gezeichnet, wenn die Kamera selbst in der Szene hängt.
  const kameraWarDraussen = camera.parent === null;
  if (kameraWarDraussen) scene.add(camera);

  const plan = avatarBauplan();
  const geometrien: BoxGeometry[] = [];
  const materialien = new Map<keyof AvatarColors, MeshLambertMaterial>();
  const gelenke = new Map<AvatarTeil, Group>();

  const materialFuer = (bereich: keyof AvatarColors): MeshLambertMaterial => {
    const vorhanden = materialien.get(bereich);
    if (vorhanden !== undefined) return vorhanden;
    const material = new MeshLambertMaterial({ color: new Color(farben[bereich]) });
    materialien.set(bereich, material);
    return material;
  };

  const wurzel = new Group();
  wurzel.name = 'arme';

  for (const teil of ARM_TEILE) {
    const gelenk = new Group();
    gelenk.name = teil;
    // Der Drehpunkt des Bauplans ist die Schulter; im Kamerabild sitzt sie seitlich unten vorn.
    gelenk.position.set(teil === 'arm links' ? -ARME.seite : ARME.seite, ARME.hoehe, ARME.tiefe);
    gelenk.rotation.x = ARME.neigung;
    wurzel.add(gelenk);
    gelenke.set(teil, gelenk);

    for (const quader of plan.teile) {
      if (quader.teil !== teil) continue;
      // Die eigenen Arme tragen nur wählbare Farben; ein Gesichtsbereich kommt an ihnen nicht vor.
      if (quader.bereich === 'gesicht') continue;
      const [sx, sy, sz] = quader.size;
      const geometry = new BoxGeometry(sx, sy, sz);
      geometrien.push(geometry);
      const mesh = new Mesh(geometry, materialFuer(quader.bereich));
      mesh.position.set(...quader.center);
      mesh.name = `${quader.teil} ${quader.bereich}`;
      gelenk.add(mesh);
    }
  }

  camera.add(wurzel);

  return {
    setFarben(neue) {
      for (const [bereich, material] of materialien) material.color.set(neue[bereich]);
    },

    setSwing(swing) {
      for (const [teil, gelenk] of gelenke) {
        gelenk.rotation.x = ARME.neigung + AVATAR_PENDEL[teil] * swing * ARME.pendel;
      }
    },

    setSichtbar(sichtbar) {
      wurzel.visible = sichtbar;
    },

    dispose() {
      wurzel.removeFromParent();
      for (const geometry of geometrien) geometry.dispose();
      geometrien.length = 0;
      for (const material of materialien.values()) material.dispose();
      materialien.clear();
      gelenke.clear();
      if (kameraWarDraussen && camera.parent === scene) scene.remove(camera);
    },
  };
}
